/**
 * Operator Practice Hooks
 *
 * TanStack Query hooks for quiz and sequencer practice features.
 * Follows the same patterns as useOperatorGoals.ts.
 *
 * - useAvailablePractice() — list available quizzes + sequencers
 * - usePracticeStats() — aggregate practice statistics
 * - useQuiz(sectionId) — load quiz questions for a section
 * - useSequencer(taskId) — load sequencer steps for a task
 * - useSubmitQuiz() — submit quiz answers (mutation)
 * - useSubmitSequencer() — submit sequencer order (mutation)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../../lib/hooks/useApi';
import type {
  QuizLoadResponse,
  SubmitQuizAttempt,
  SubmitQuizResponse,
  SequencerLoadResponse,
  SubmitSequencerAttempt,
  SubmitSequencerResponse,
  PracticeStats,
  AvailablePracticeResponse,
} from '@guild/shared';

// ============================================
// QUERY KEYS
// ============================================

export const practiceKeys = {
  all: ['operator-practice'] as const,
  available: () => [...practiceKeys.all, 'available'] as const,
  stats: () => [...practiceKeys.all, 'stats'] as const,
  quiz: (sectionId: string) => [...practiceKeys.all, 'quiz', sectionId] as const,
  sequencer: (taskId: string) => [...practiceKeys.all, 'sequencer', taskId] as const,
};

// ============================================
// QUERIES
// ============================================

/**
 * Fetch available quizzes and sequencers for the operator.
 * Returns lists of quiz-eligible sections and sequencer-eligible tasks.
 */
export function useAvailablePractice() {
  const api = useApi();

  return useQuery({
    queryKey: practiceKeys.available(),
    queryFn: () => api.get<AvailablePracticeResponse>('/api/operator/practice/available'),
  });
}

/**
 * Fetch aggregate practice statistics for the operator.
 * Returns attempt counts, average/best scores, last practice date.
 */
export function usePracticeStats() {
  const api = useApi();

  return useQuery({
    queryKey: practiceKeys.stats(),
    queryFn: () => api.get<PracticeStats>('/api/operator/practice/stats'),
  });
}

/**
 * Load quiz questions for a given section.
 * Only enabled when sectionId is provided.
 * staleTime: 0 to always get fresh random selection on re-fetch.
 */
export function useQuiz(sectionId: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: practiceKeys.quiz(sectionId ?? ''),
    queryFn: () => api.get<QuizLoadResponse>(`/api/operator/practice/quiz/${sectionId!}`),
    enabled: !!sectionId,
    staleTime: 0, // Always re-fetch for fresh random selection
  });
}

/**
 * Load sequencer steps for a given task.
 * Only enabled when taskId is provided.
 * staleTime: 0 to always get fresh shuffle on re-fetch.
 */
export function useSequencer(taskId: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: practiceKeys.sequencer(taskId ?? ''),
    queryFn: () => api.get<SequencerLoadResponse>(`/api/operator/practice/sequencer/${taskId!}`),
    enabled: !!taskId,
    staleTime: 0,
  });
}

// ============================================
// MUTATIONS
// ============================================

/**
 * Submit quiz answers for server-side grading.
 * Invalidates stats and available caches on success (new attempt recorded).
 */
export function useSubmitQuiz() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SubmitQuizAttempt) =>
      api.post<SubmitQuizResponse>('/api/operator/practice/quiz', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: practiceKeys.stats() });
      queryClient.invalidateQueries({ queryKey: practiceKeys.available() });
    },
  });
}

/**
 * Submit sequencer step order for server-side grading.
 * Invalidates stats and available caches on success.
 */
export function useSubmitSequencer() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SubmitSequencerAttempt) =>
      api.post<SubmitSequencerResponse>('/api/operator/practice/sequencer', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: practiceKeys.stats() });
      queryClient.invalidateQueries({ queryKey: practiceKeys.available() });
    },
  });
}
